/**
 * Ground Truth Status Configuration
 * 
 * Centralized configuration for Ground Truth Status priorities, colors, and rules.
 * Update this file when priority hierarchy changes - no code changes needed elsewhere.
 * 
 * Last Updated: April 10, 2026
 * Reason: Changed priority to prioritize NEW_FIELD (P1) over FIELD_MISSING (P2)
 *         to ensure red indicator is visible when new fields are added.
 */

import { GroundTruthStatusType } from '../models/truth-management.model';

/**
 * Status Configuration Interface
 */
export interface StatusConfig {
  status: GroundTruthStatusType;
  priority: number;  // Lower number = Higher priority (1 is highest)
  color: 'green' | 'grey' | 'red' | 'yellow' | 'lightblue' | 'orange';
  label: string;
  description: string;
  messageTemplate: string;
  detailsTemplate: string;
}

/**
 * Ground Truth Status Priority Configuration
 * 
 * EXCLUSIVE STATUSES (cannot overlap with others):
 * - HEALTHY: All conditions met, no issues
 * - NO_SAMPLE: No samples uploaded
 * 
 * PRIORITY STATUSES (can overlap, shown by highest priority):
 * - P1: FIELD_MISSING (Yellow) - Required fields not filled in any sample
 * - P2: FIELD_UPDATE (Orange) - Invalid enum values in any sample
 */
export const GROUND_TRUTH_STATUS_CONFIG: Record<GroundTruthStatusType, StatusConfig> = {
  // Exclusive Statuses
  HEALTHY: {
    status: 'HEALTHY',
    priority: 999,  // Shown only when no issues exist
    color: 'green',
    label: 'Healthy',
    description: 'All fields updated and enums filled as per mapping in all samples',
    messageTemplate: 'All samples complete and valid',
    detailsTemplate: '{{count}} samples, {{keyCount}} fields'
  },
  
  NO_SAMPLE: {
    status: 'NO_SAMPLE',
    priority: 998,  // Exclusive - shown when no samples exist
    color: 'grey',
    label: 'No Sample',
    description: 'No sample attached',
    messageTemplate: 'No samples uploaded',
    detailsTemplate: 'Upload sample files to begin'
  },
  
  // Priority Statuses (P1 = highest priority)
  FIELD_MISSING: {
    status: 'FIELD_MISSING',
    priority: 1,  // P1 - HIGHEST PRIORITY (Yellow)
    color: 'yellow',
    label: 'Field Missing',
    description: 'Ground truth not updated for any of the fields in any of the samples',
    messageTemplate: 'Fields missing in samples',
    detailsTemplate: '{{count}} sample(s) have incomplete data'
  },
  
  FIELD_UPDATE: {
    status: 'FIELD_UPDATE',
    priority: 2,  // P2 (Orange)
    color: 'orange',
    label: 'Field Update',
    description: 'Enum field needs update for any of the samples',
    messageTemplate: 'Invalid enum values',
    detailsTemplate: '{{count}} sample(s) have values that need correction'
  }
};

/**
 * Get status configuration by status type
 */
export function getStatusConfig(status: GroundTruthStatusType): StatusConfig {
  return GROUND_TRUTH_STATUS_CONFIG[status];
}

/**
 * Get status priority (for sorting/comparison)
 */
export function getStatusPriority(status: GroundTruthStatusType): number {
  return GROUND_TRUTH_STATUS_CONFIG[status].priority;
}

/**
 * Get status color
 */
export function getStatusColor(status: GroundTruthStatusType): 'green' | 'grey' | 'red' | 'yellow' | 'lightblue' | 'orange' {
  return GROUND_TRUTH_STATUS_CONFIG[status].color;
}

/**
 * Get formatted message with template variables replaced
 */
export function getStatusMessage(
  status: GroundTruthStatusType, 
  variables: { count?: number; keyCount?: number }
): { message: string; details: string } {
  const config = GROUND_TRUTH_STATUS_CONFIG[status];
  
  let message = config.messageTemplate;
  let details = config.detailsTemplate;
  
  // Replace template variables
  if (variables.count !== undefined) {
    message = message.replace('{{count}}', String(variables.count));
    details = details.replace('{{count}}', String(variables.count));
  }
  
  if (variables.keyCount !== undefined) {
    details = details.replace('{{keyCount}}', String(variables.keyCount));
  }
  
  return { message, details };
}

/**
 * Sort statuses by priority (ascending = highest priority first)
 */
export function sortByPriority(
  a: GroundTruthStatusType, 
  b: GroundTruthStatusType
): number {
  return getStatusPriority(a) - getStatusPriority(b);
}

/**
 * Priority order for UI sorting (for table sorting)
 * Lower index = shown first when sorting ascending
 */
export const STATUS_SORT_ORDER: Record<GroundTruthStatusType, number> = {
  'HEALTHY': 1,
  'NO_SAMPLE': 2,
  'FIELD_MISSING': 3,   // P1 - Most critical
  'FIELD_UPDATE': 4     // P2 - Least critical
};
